import * as api from './api'

/**
 * A client's collected ledgers, one folder per cycle, paginated.
 *
 * Backed by `GET /ledger/clients/:clientCode/documents` — one `LedgerRequest`
 * is one folder, dated by when their statement arrived. Grouping is a Mongo
 * query on the backend, not a naming scheme in S3: files are stored flat and
 * resolved by the id stored on each request, never by listing a bucket prefix.
 */

/**
 * One page of a client's ledger folders.
 *
 * @param {object} p
 * @param {object} p.request              the ledger request (for its clientCode)
 * @param {string} [p.from]               YYYY-MM-DD inclusive lower bound on receivedOn
 * @param {string} [p.to]                 YYYY-MM-DD inclusive upper bound
 * @param {'newest'|'oldest'} [p.sortBy]
 * @param {number} [p.page]               1-based
 * @param {number} [p.limit]
 * @returns {Promise<{folders, page, limit, total, hasMore}>}
 */
export async function fetchClientLedgers({ request, from = '', to = '', sortBy = 'newest', page = 1, limit = 5 }) {
  return api.listClientDocuments(request.clientCode, { from, to, sortBy, page, limit })
}

/**
 * The bytes of one ledger file — the client's statement, through the same
 * authenticated proxy the list screen's own View button uses.
 */
export function fetchLedgerFileBytes({ file }) {
  return api.fetchStatementBytes(file.requestId)
}

/**
 * Every folder matching a filter, ignoring pagination.
 *
 * Download-all means all of them, not just the page on screen — so it walks the
 * pages rather than trusting whatever the list happens to have loaded.
 */
export async function fetchAllClientLedgers({ request, from = '', to = '', sortBy = 'newest' }) {
  const out = []
  let page = 1
  for (;;) {
    const res = await fetchClientLedgers({ request, from, to, sortBy, page, limit: 50 })
    out.push(...res.folders)
    if (!res.hasMore) return out
    page += 1
    // A guard, not a limit anyone should hit: a client with 50 pages of ledgers
    // is a data problem, and looping forever on a paging bug is worse.
    if (page > 40) return out
  }
}
